// English texts for weather and prayer times (see weatherPrayer.nl.ts for the rules: neutral
// variants for families without the prayer module, calm child sentences that never overrule the
// parent, the call word and prayer names follow the chosen naming).
export const weatherPrayerEn = {
    geo: {
        title: 'Location',
        intro: 'Choose your postcode or town. We only keep an area of about one kilometre.',
        searchLabel: 'Postcode or town',
        searchPlaceholder: 'For example 3511 AA or Utrecht',
        searchButton: 'Search',
        searchHint: 'No house number needed; we do not send it either.',
        noResults: 'Nothing found. Try a postcode or a town name.',
        tooMany: 'Easy does it: try again in a minute.',
        chosen: 'Area: {label}',
        privacy: 'We never keep a street, house number or exact spot. Linked family, guests and calendar links never see this.',
        remove: 'Remove location',
        removed: 'Location removed.',
        variants: {
            weather: {
                settingsBody: 'For the weather on the board',
                why: 'Why? For the weather on the board. Nothing else.',
                removeConfirm: 'We remove the location of your family and of the tablets. The weather pauses until you choose a new one. Continue?',
                askBody: 'Once, for the weather. We only keep an area of about one kilometre.',
            },
            prayer: {
                settingsBody: 'For the prayer times',
                why: 'Why? For the prayer times. Nothing else.',
                removeConfirm: 'We remove the location of your family and of the tablets. The prayer times pause until you choose a new one. Continue?',
                askBody: 'Once, for the times on the board. We only keep an area of about one kilometre.',
            },
            both: {
                settingsBody: 'For the weather and the prayer times',
                why: 'Why? For the weather on the board and the prayer times. Nothing else.',
                removeConfirm: 'We remove the location of your family and of the tablets. Weather and prayer times pause until you choose a new one. Continue?',
                askBody: 'Once, for the weather and the times on the board. We only keep an area of about one kilometre.',
            },
        },
        device: {
            title: 'Location of this tablet',
            household: 'Same as the family',
            own: 'Use this tablet’s location',
            ownHint: 'Handy if this tablet is somewhere else, for example in a holiday home.',
            otherHome: 'Is the tablet in someone else’s home? Then we also keep an area of about one kilometre for that home. Let them know.',
            pending: 'A question appears on the tablet. Tap "Yes, once" there.',
            measured: 'Area of this tablet: {label}',
            far: 'This tablet is about {km} km from your family address.',
            remeasure: 'Measure again',
            failed: 'The tablet could not find its location. We use the family location.',
        },
        kiosk: {
            askTitle: 'May this tablet use its location?',
            yes: 'Yes, once',
            no: 'No',
            busy: 'Looking…',
            done: 'Done: {label}',
            failed: 'That did not work. We use the family location.',
        },
        web: {
            choose: 'Choose a location',
            change: 'Change location',
            none: 'No location chosen yet.',
            area: 'Area: {label}',
            tablets: 'Tablets',
            tabletsNone: 'No tablets paired yet.',
            edit: 'Change',
            save: 'Save',
            saved: 'Saved',
            cancel: 'Cancel',
            country: 'Country',
            countryNl: 'The Netherlands',
            countryOther: 'Another country',
            city: 'Town or city',
            postcode: 'Postcode',
            countryCode: 'Country code (e.g. BE or DE)',
            searching: 'Searching…',
            pick: 'Choose your area',
            removeTitle: 'Remove location?',
            unavailable: 'Searching does not work right now. Please try again later.',
            requested: 'Measurement requested',
            deviceSame: 'Same as the family',
            deviceSameWith: 'Same as the family ({label})',
        },
        credits: {
            pdok: 'Location data: PDOK/Kadaster',
            osm: '© OpenStreetMap contributors (ODbL)',
        },
    },
    weather: {
        title: 'Weather',
        settingsBody: 'Coat, boots or sunscreen? Advice for the moments the children head out',
        enabled: 'Show weather',
        needsLocation: 'Choose a location first, then we show the weather.',
        showInBanners: 'In the departure banner ("8:15 to school: raincoat and boots")',
        adjust: 'Adjust',
        morning: 'The morning runs from {start} to {end}',
        thresholdsTitle: 'When which advice',
        thresholdsNote: 'Sun, heat, wind and slippery roads follow RIVM and KNMI. The limits for coats and jumpers are our own defaults: feel free to change them.',
        thresholds: {
            sweaterBelowC: 'Cardigan or jumper below {value} °C (feels like)',
            coatBelowC: 'Coat below {value} °C',
            thickCoatBelowC: 'Warm coat below {value} °C',
            hatGlovesAtOrBelowC: 'Hat and gloves at {value} °C and colder',
            umbrella: 'Suggest an umbrella when it rains and there is little wind',
            shortsFromC: 'Shorts are fine from {value} °C and dry',
            waterBottleFromC: 'Water bottle from {value} °C',
        },
        perChild: 'Per child',
        coldDelta: {
            label: 'Gets cold easily',
            value: { one: 'coat {count} degree sooner', other: 'coat {count} degrees sooner' },
            warmer: { one: 'coat {count} degree later', other: 'coat {count} degrees later' },
            none: 'Like everyone',
            hint: 'Does a child get cold easily? Set a higher number: they get a coat that many degrees earlier. Lower: later.',
        },
        uvClearSky: 'UV index {value} (clear sky)',
        uvCloud: 'UV index {value}',
        feelsLike: 'Feels like {value} °C',
        wind: 'Wind force {bft}',
        stale: 'Weather unknown for now',
        attribution: 'Weather data: MET Norway',
        feel: { cold: 'cold', chilly: 'chilly', mild: 'nice', warm: 'warm', hot: 'hot' },
        itemLabels: {
            warning: 'Careful',
            thunder: 'Thunder',
            very_windy: 'Strong wind',
            slippery: 'Slippery',
            very_cold: 'Very cold',
            thick_coat: 'Warm coat',
            coat: 'Coat',
            sweater: 'Jumper',
            raincoat: 'Raincoat',
            boots: 'Boots',
            hat_gloves: 'Hat and gloves',
            no_umbrella: 'Hood up',
            umbrella: 'Umbrella',
            windy: 'Wind',
            shade: 'Out of the sun',
            sunscreen: 'Sunscreen',
            sun_hat: 'Cap on',
            extra_drinks: 'Drink lots',
            water_bottle: 'Water bottle',
            midday_shade: 'Shade',
            reapply: 'Sunscreen again',
            shorts: 'Shorts',
        },
        itemWords: {
            warning: 'careful',
            thunder: 'go inside when it rumbles',
            very_windy: 'very strong wind: hold on tight on the bike',
            slippery: 'it may be slippery: walk and cycle slowly',
            very_cold: 'very cold: only briefly outside',
            thick_coat: 'warm coat',
            coat: 'coat',
            sweater: 'jumper',
            raincoat: 'raincoat',
            boots: 'boots',
            hat_gloves: 'hat and gloves',
            no_umbrella: 'no umbrella, hood up',
            umbrella: 'umbrella',
            windy: 'strong wind: careful on the bike',
            shade: 'out of the sun',
            sunscreen: 'sunscreen',
            sun_hat: 'cap on',
            extra_drinks: 'extra drinks and play quietly in the shade',
            water_bottle: 'take a water bottle',
            midday_shade: 'play in the shade between 12 and 3',
            reapply: 'sunscreen again at 12',
            shorts: 'shorts are fine',
        },
        waterproof: 'warm waterproof coat',
        headline: {
            rain: 'At {time} it rains: {items}.',
            wet: 'It may be wet this morning: {items}.',
            wetLater: 'Around {time} it may be wet: {items}.',
            snow: 'At {time} it snows: {items}.',
            thunder: 'At {time} there may be thunder: go inside when it rumbles.',
            cold: 'Cold outside at {time}: {items}.',
            hot: 'It is getting warm: {items}.',
            coolThenWarm: 'Cool at {time}, warm later: {items}.',
            sun: 'Sunny: {items}.',
            wind: 'Strong wind at {time}: careful on the bike.',
            nice: 'At {time}: {feel} weather.',
            niceWith: 'At {time}: {feel} weather, {items}.',
            warning: '{kids}',
            departure: 'At {time} to {title}: {items}.',
        },
        and: 'and',
        warningKids: {
            wind: 'It is very windy. Zip up your coat and stay close to mum or dad.',
            rain: 'Lots of rain today. Raincoat on and boots too.',
            thunder: 'There may be thunder. When it rumbles, we go inside.',
            snow: 'Lots of snow. Boots on and walk slowly.',
            ice: 'It is slippery outside. Walk slowly, no running.',
            heat: 'It is very hot. Drink lots and play in the shade.',
            cold: 'It is very cold. Dress warmly: hat and gloves.',
            fog: 'It is foggy. Stay close to mum or dad.',
            other: 'Pay a little extra attention to the weather today.',
        },
        warningLevel: { yellow: 'Code yellow', orange: 'Code orange', red: 'Code red' },
        web: {
            todayTitle: 'Weather today',
            todayTitleAt: 'Weather today · {label}',
            tomorrowTitle: 'Weather tomorrow',
            tomorrowTitleAt: 'Weather tomorrow · {label}',
            nowLabel: 'Now',
            tomorrowLabel: 'Tomorrow',
            invite: 'Would you like weather advice on the board?',
            inviteBody: 'Coat, boots or sunscreen: we say it at the moments the children leave the house.',
            notNow: 'Not now',
            chooseLocation: 'Choose a location',
            settings: 'Settings',
            showOnKiosk: 'On the board',
            save: 'Save',
            saved: 'Saved',
            resetDefaults: 'Reset to defaults',
            morningStart: 'Morning from',
            morningEnd: 'Morning until',
            alsoFor: '{name}: {items}',
            everyone: 'Everyone',
            tempNow: '{temp}°',
            staleBody: 'We will fetch the weather again as soon as possible.',
            childNone: 'No children in the family yet.',
            offTitle: 'Turn off the weather?',
            offConfirm: 'Nothing else uses the location. We will also delete the location of your family and of the tablets. Continue?',
            offConfirmButton: 'Turn off and delete location',
            offNotice: 'The weather is off.',
            offNoLocation: 'The weather is off. Choose a location; then turn the weather on here with one tap.',
            turnOn: 'Turn on the weather',
            less: 'Less',
            more: 'More',
        },
        warningSource: {
            knmi: 'Source: KNMI',
            meteoalarm: 'Source: KNMI via MeteoAlarm',
            fake: 'Source: test',
        },
        /** Precipitation sentence above the chart of the Precipitation widget. */
        precip: {
            dryAll: 'Dry for the next {hours} hours.',
            nowUntil: 'Now {kind}, dry at {end}.',
            nowLong: '{kind} for the next few hours.',
            fromUntil: '{kind} from {start}, dry at {end}.',
            dryUntil: 'Dry until {start}, then {kind}.',
            words: {
                drizzle: 'drizzle',
                rain_light: 'light rain',
                rain: 'rain',
                rain_heavy: 'heavy rain',
                sleet: 'sleet',
                snow: 'snow',
                hail: 'hail',
                thunder: 'thundershowers',
            },
        },
    },
    prayer: {
        title: 'Prayer times',
        settingsBody: 'The times for your area, on the living room board',
        enabled: 'Use prayer times',
        needsLocation: 'Choose a location first. Then we calculate the times for your area.',
        privacy: 'This belongs to "What suits our family". Only your parents see the settings; linked family and guests never do.',
        boardVisible: 'Note: the times and the card are on the board, so everyone in the room sees them (visitors and babysitters too).',
        call: { turkish: 'ezan', arabic: 'adhan' },
        callTitle: { turkish: 'Ezan', arabic: 'Adhan' },
        setup: {
            quickTitle: 'Prayer times',
            quickBody: 'We calculate with {method}. Does that match your mosque? Then you are done. Fine-tuning and sound per prayer can come later.',
            quickNoCall: 'No {call} recording is included yet. Until you upload your own, the living room tablet plays a soft chime.',
            quickTone: 'The soft chime is currently chosen under "Which recording?". Want to hear the {call}? Choose the included recording there via "More settings".',
            quickTimes: 'Today in {place}',
            quickDone: 'Done',
            quickMore: 'More settings',
            step1: 'How do we calculate the times?',
            step1Body: 'Mosques do not all calculate the same way. Choose what your mosque uses, or keep our choice.',
            step2: 'Match your own mosque',
            step2Body: 'Put your mosque’s timetable next to ours. If a time differs, shift it. In summer mosques sometimes differ by half an hour or more: set it per month, or import the yearly timetable.',
            step3: 'What sounds on the tablet?',
            step3Body: 'The {call} plays on the living room tablet. Other tablets stay quiet unless you switch them on.',
            step3BodyNoCall: 'There is no {call} recording yet. Until you upload your own below, "{callTitle}" plays the soft chime. Other tablets stay quiet unless you switch them on.',
            step3BodyTone: '"{callTitle}" currently plays the soft chime, because that is chosen under "{step4}". Other tablets stay quiet unless you switch them on.',
            step4: 'Which recording?',
            done: 'Done. The next prayer is now on the board.',
        },
        method: {
            diyanet: 'Diyanet (Turkey)',
            mwl: 'Muslim World League',
            uoif: 'UOIF (France)',
            morocco: 'Morocco',
            jafari: 'Jafari (Shia)',
            eu_joint: 'European joint calendar (Diyanet · Milli Görüş)',
        },
        methodInSentence: {
            diyanet: 'the Diyanet calendar (Turkey)',
            mwl: 'Muslim World League',
            uoif: 'UOIF (France)',
            morocco: 'the Moroccan method',
            jafari: 'the Jafari method (Shia)',
            eu_joint: 'the European joint calendar (Diyanet · Milli Görüş)',
        },
        methodHint: {
            diyanet: 'The calendar for Turkey. Turkish mosques in Europe use the European calendar above.',
            mwl: 'Widely used in Europe. A good default if you are not sure.',
            uoif: 'Later Fajr and earlier Isha (12°).',
            morocco: 'What Moroccan mosques often use.',
            jafari: 'Maghrib after dusk, about a quarter of an hour after sunset.',
            eu_joint: 'Since 2023 the same calendar for Diyanet (ISN, DITIB) and Milli Görüş in Europe. Choose this if your mosque is Turkish.',
        },
        asrSchool: { standard: 'Standard (first time)', hanafi: 'Hanafi (later in the afternoon)' },
        asrSource: {
            title: 'Time of {asr}',
            madhab: 'Follows your madhab',
            manual: 'Set by you',
            unset: 'Default',
            follow: 'Follow the madhab again',
            madhabLink: 'Choose or change the madhab',
            why: {
                hanafi: 'You follow the Hanafi school: {asr} later in the afternoon.',
                other_madhab: 'You follow the {madhab} school: {asr} at the standard time.',
                calendar: 'You follow the Hanafi school. The calendar of Diyanet and Milli Görüş gives the first time for {asr} (asr-ı evvel), just like your mosque. Prefer the later time (asr-ı sani)? Choose "{hanafi}" above.',
                no_madhab: 'No madhab chosen yet: {asr} at the standard time.',
            },
            manualWhy: 'You chose this yourself. Another madhab does not change it.',
        },
        madhabSuggestion: {
            body: 'You follow the Hanafi school. Turkish mosques in Europe calculate with {method}.',
            apply: 'Use this calendar',
            applied: 'We now calculate with {method}.',
        },
        naming: { turkish: 'Turkish names', arabic: 'Arabic names' },
        namingTitle: 'Names of the prayers',
        names: {
            turkish: {
                fajr: 'Sabah',
                sunrise: 'Güneş',
                dhuhr: 'Öğle',
                asr: 'İkindi',
                maghrib: 'Akşam',
                isha: 'Yatsı',
            },
            arabic: {
                fajr: 'Fajr',
                sunrise: 'Sunrise',
                dhuhr: 'Dhuhr',
                asr: 'Asr',
                maghrib: 'Maghrib',
                isha: 'Isha',
            },
        },
        cardNames: {
            turkish: { fajr: 'Sabah', dhuhr: 'Öğle', asr: 'İkindi', maghrib: 'Akşam', isha: 'Yatsı' },
            arabic: { fajr: 'Fajr', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha' },
        },
        sunriseNote: 'Sunrise is the end of the time for {fajr}. Nothing ever sounds then.',
        offsets: {
            title: 'Shift per prayer',
            sameAllYear: 'The same all year',
            perMonth: 'Per month',
            month: 'In {month}',
            later: '{value} min later',
            earlier: '{value} min earlier',
            seeded: 'Matched to the Diyanet timetable for Rotterdam, only checked for September. Check it with your own mosque, especially in summer.',
            tableDay: 'These times come from your mosque’s timetable. Offsets only apply to days that are not in the timetable.',
            compare: 'Compare with your mosque’s timetable',
            dstCheck: 'The clocks changed. Does it still match your mosque’s timetable?',
            dstYes: 'Yes, it matches',
            dstAdjust: 'Adjust',
        },
        table: {
            title: 'Import your mosque’s timetable',
            body: 'Paste the yearly timetable (per line: date; {fajr}; sunrise; {dhuhr}; {asr}; {maghrib}; {isha}). A day from the timetable replaces our calculation.',
            read: 'Import',
            result: { one: '{count} day imported', other: '{count} days imported' },
            badLines: 'Could not read: line {lines}',
            fromTable: 'From your mosque’s timetable',
            remove: 'Remove timetable',
        },
        sound: {
            adhan: '{callTitle}',
            adhanPending: '{callTitle} (no recording yet: the chime for now)',
            adhanTone: '{callTitle} (the chime for now, see "{step4}")',
            chime: 'Soft chime',
            silent: 'Silent (card only)',
        },
        yearRange: '{name}: in {month} around {time}',
        fajrWarning: '{fajr} is before sunrise. The {call} then wakes the whole house.',
        /** One line: never the regular call at Fajr (founder decision 2026-09-25). */
        fajrNeedsRecording: '{fajr} has its own call (with "as-salatu khayrun min an-nawm"), so the regular call never plays there. Upload your own recording for {fajr}, or choose the chime.',
        quiet: {
            title: 'Late evening and night',
            body: 'After {from} and until {until}, instead of the {call}:',
            chime: 'a soft chime',
            silent: 'nothing (only the card in the living room)',
            adhan: 'the {call} anyway',
            hint: 'In June {isha} falls around midnight and {maghrib} after ten. With the chime the house stays quiet.',
        },
        /** Eenmalige melding na het overzetten naar de gezamenlijke kalender (0041). */
        methodNotice: 'Prayer times now follow the European calendar of Diyanet and Milli Görüş; in summer {fajr} and {isha} are therefore different.',
        methodNoticeOk: 'OK',
        /** Eerlijk over eu_joint (gefit op de Diyanet-tabellen, niet officieel; review g6b fs r1). */
        methodApprox: 'We calculate the joint calendar ourselves. For Rotterdam and Amsterdam it matches the published table within 1 or 2 minutes all year. No prayer time and no adhan on the tablet comes before the Diyanet table: we round up, and for {fajr} we add 2 minutes, 10 from mid-April to mid-September. Imsak, the start of the fast, is never later than the table; during Ramadan the board shows both imsak and {fajr}. Elsewhere, especially from mid-April to mid-September, your mosque may differ by a few minutes (up to about 9). Adjust to your mosque, for example with a few minutes for {fajr} under Shift per prayer, or import your mosque’s timetable.',
        /** Korte zin bij de tijden (Vandaag, tablet) in de zomerweken van eu_joint. */
        methodTune: 'Summer times: your mosque may differ by a few minutes. Adjust to your mosque.',
        /** Opnamebibliotheek (golf 6b, DESIGN §8.GB.15). */
        library: {
            intro: 'Choose a recording per region. Have a listen first: these are real recordings, sometimes with city sounds or birds.',
            regions: {
                arabic: 'Arabic style',
                hijaz: 'Mecca (Hijaz)',
                turkish: 'Istanbul and Turkey',
                maghreb: 'Morocco',
                egypt: 'Egypt',
                levant: 'Jordan',
                southeast_asia: 'Malaysia',
                central_asia: 'Kazakhstan',
                europe: 'Europe',
                other: 'Other (place unknown)',
            },
            places: {
                mecca: 'Mecca, Masjid al-Haram',
                istanbul: 'Istanbul (short)',
                istanbul_fatih: 'Istanbul, Kalenderhane Mosque',
                mardin: 'Mardin, morning',
                patara: 'Patara, village, morning',
                casablanca: 'Casablanca, Hassan II Mosque',
                marrakech: 'Marrakech, Jemaa el-Fna',
                marrakech_medina: 'Marrakech, medina, morning',
                quseir: 'El Quseir, by the sea',
                amman: 'Amman, morning',
                kuala_lumpur: 'Kuala Lumpur, morning',
                shalqar: 'Shalqar',
                malmo: 'Malmö, morning',
                indoor: 'Friday, recorded indoors',
            },
            option: '{place} ({year})',
            meta: '{author} · {license} · {duration}',
            fajrTitle: 'Recording for {fajr}',
            fajrBody: '{fajr} has its own call, with "as-salatu khayrun min an-nawm". Choose a morning recording or upload your own; without one, {fajr} gets the chime.',
            fajrNone: 'None (the chime)',
            fajrUnverified: 'morning recording, not yet checked for the morning phrase',
            sources: 'Sources and licences',
            sourcesIntro: 'All recordings are free to use. For each one you see the title, the maker, the source and the licence. We removed silence at the start and end, made them equally loud and converted them to AAC.',
            by: '"{title}" by {author}, via {platform}',
            shareAlike: 'Our edited version is also licensed under {license}.',
            report: 'Is a source wrong? Email us at support@amumin.com and we will remove the recording.',
        },
        audio: {
            builtin_a: '{callTitle}, Arabic style',
            builtin_b: '{callTitle}, Arabic style (shorter)',
            sunniNote: 'These recordings have the Sunni text.',
            shiaNote: 'Our recordings have the Sunni text. Choose the chime or your own recording.',
            tone: 'Soft chime (its own sound, different from the task chimes)',
            own: 'Upload your own recording (m4a or mp3, up to 5 minutes)',
            ownFajr: 'Your own recording for {fajr}',
            credits: 'About the recordings',
        },
        kidsMayStop: 'Children may stop the {call}',
        kidsMayStopHint: 'Off: the Stop button is with the parent controls.',
        device: {
            title: 'On which tablets?',
            sound: 'Play the {call}',
            soundAny: 'Sound at prayer time',
            line: 'Next prayer in the header',
            card: 'Card at prayer time',
            cardNight: 'Never a card at night in a bedroom.',
            volume: 'Volume',
            volumeIos: 'On an iPad you set the volume with the buttons on the side.',
            test: 'Test on the tablet',
            locked: '{name}: sound is off. Tap the screen of that tablet once.',
            unsupported: '{name}: this tablet cannot play sound.',
            outcome: {
                played: 'Played at {time}',
                card_locked: 'Silent at {time}: sound was off',
                card_late: 'Silent at {time}: seen too late',
                load_failed: 'Silent at {time}: recording did not load',
                skipped: 'Skipped at {time}',
            },
            checklist: 'Keep the screen on (Auto-Lock: Never). In Guided Access: Sleep/Wake button off, volume buttons on. Do not put the iPad to sleep with the top button: then {isha} and {fajr} stay silent.',
        },
        kiosk: {
            next: '{name} {time}',
            nextIn: '{name} {time} · in {left}',
            inMinutes: 'in {n} min',
            inHours: 'in {n} h',
            now: 'Now: {name}',
            cardTitle: 'Time for {name}',
            playing: '{callTitle} playing',
            playingChime: 'Chime playing',
            cardStop: 'Stop',
            cardStopHold: 'Hold to stop',
            cardClose: 'Close',
            soundLocked: 'Tap for sound',
            soundLockedLong: 'Sound is off. Tap to turn it on.',
            unsupported: 'This tablet cannot play sound',
            unknown: 'Prayer times unknown for now',
        },
        ramadan: {
            word: { turkish: 'Ramazan', arabic: 'Ramadan' },
            imsak: { turkish: 'İmsak', arabic: 'Imsak' },
            sabah: { turkish: 'Sabah', arabic: 'Fajr' },
            iftar: { turkish: 'İftar', arabic: 'Iftar' },
            day: '{word} · day {n}',
            last: '{word} · last day',
            countdown: '{n} min to go',
            now: 'Time for {iftar}',
            tomorrow: 'Tomorrow',
            eve: '{word} starts tomorrow',
        },
        question: {
            title: 'Prayer times on the board?',
            body: 'The next prayer then shows at the top of the board, and at prayer times the {call} plays on the living-room tablet.',
            body2: '{fajr} is silent by default. You can change everything later.',
            yes: 'Yes, please',
            no: 'No, not now',
            declined: 'You can switch it on later in Settings > Prayer times.',
            placeTitle: 'Where do you live?',
            placeBody: 'Then we work out the times for your area. A postcode or town is enough; we only keep an area of about a kilometre.',
            placeOther: 'Outside the Netherlands, or a tablet somewhere else? Set that in Settings > Location.',
            placeSave: 'Use this place',
            placeChosen: 'Times for {label}',
            /** Golf 6c: waarom deze methode (de mazhab, nooit de keuken). */
            methodMadhab: 'We calculate with {method}, because you follow the Hanafi school.',
            methodDefault: 'We calculate with {method}.',
            change: 'Change',
            methodChoose: 'Calculation method',
            becauseOne: 'Because {name} follows Islam.',
            becauseMany: 'Because {names} follow Islam.',
            next: 'Continue',
            done: 'Done',
            enabled: 'The next prayer is now on the board.',
            renew: 'Give consent and switch on',
            off: 'Prayer times are off. The board does not show them and nothing plays.',
            turnOn: 'Switch prayer times on',
            offBody: 'Rather do without for a while? The times leave the board and nothing plays. Your method and sound choices are kept; if the weather is off too, we delete the location.',
            turnOff: 'Switch prayer times off',
            turnOffConfirm: 'Switch prayer times off? You can switch them on here again at any time.',
            turnOffYes: 'Yes, switch off',
            turnOffCancel: 'Keep them on',
            renewOnly: 'Give consent',
            renewBody: 'The explanation for "What fits our family" has changed: it now also mentions extra parts on the board, such as these prayer times. Give consent again here to keep using them.',
        },
        web: {
            consentRenewal: 'The explanation for "What fits our family" has changed: it now also mentions extra parts on the board, such as these prayer times. Please give consent again there first.',
            consentLink: 'Go to What fits our family',
            locationLink: 'Choose a location',
            save: 'Save',
            saved: 'Saved',
            today: 'Today, {place}',
            ourTime: 'calculated',
            offset: 'offset',
            month: 'Month',
            tableDays: {
                one: '{count} day from your mosque timetable',
                other: '{count} days from your mosque timetable',
            },
            tablePlaceholder: '2026-10-01;05:40;07:30;13:35;16:45;19:15;20:50',
            listen: 'Listen',
            /** Tijdens het afspelen (review g6b ux r1: een stopknop, geen luidspreker met streep). */
            listenStop: 'Stop',
            listenPlaying: 'Playing',
            upload: 'Choose file',
            uploaded: 'Own recording in use',
            uploadFailed: 'Only an m4a or mp3 file of at most 5 minutes and 5 MB.',
            last: 'Last: {items}',
            testPending: 'Test sent. The result appears here within a minute.',
            uploadCall: 'Upload your own {call} recording',
            testResult: {
                played: 'Played',
                card_locked: 'Silent: sound was off',
                load_failed: 'Silent: recording not loaded',
                skipped: 'Skipped',
            },
            volume: 'Volume (Android, Fully Kiosk)',
            noTablets: 'No tablet paired yet.',
            /** "About the recordings": where the included recording comes from (docs/LICENSES.md). */
            credits: {
                builtin: '{callTitle}, Arabic style: "{title}" by {author}, on',
                license: 'CC0 1.0 (public domain)',
                processing: 'We converted the recording to AAC and MP3 (mono) and made it as loud as the chime. Nothing else was changed.',
                tone: 'The soft chime is AMUMIN’s own sound.',
            },
        },
        attribution: 'Prayer times: own calculation · method {method}',
        attributionTable: 'Prayer times: your mosque’s timetable',
        hijri: {
            months: [
                'Muharram',
                'Safar',
                'Rabi’ al-Awwal',
                'Rabi’ al-Akhir',
                'Jumada al-Ula',
                'Jumada al-Akhirah',
                'Rajab',
                'Sha’ban',
                'Ramadan',
                'Shawwal',
                'Dhu al-Qa’dah',
                'Dhu al-Hijjah',
            ],
            pattern: '{day} {month} {year} AH',
        },
    },
};
//# sourceMappingURL=weatherPrayer.en.js.map